// Storage Summary — Step 5 Results.
// Displays per-site storage sizing outcomes with usable → raw waterfall.

import type { StorageResult } from '../../types';

interface Props {
  storage: StorageResult[];
}

export function StorageSummary({ storage }: Props) {
  if (storage.length === 0) {
    return <p style={{ color: '#64748b', fontStyle: 'italic' }}>No storage results available.</p>;
  }

  const primary = storage.find(r => r.site === 'primary');
  const dr = storage.find(r => r.site === 'dr');

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20 }}>
      {primary && <StorageCard result={primary} label="Primary Site" colour="#065f46" />}
      {dr      && <StorageCard result={dr}      label="DR Site"      colour="#9d174d" />}
    </div>
  );
}

function StorageCard({ result, label, colour }: { result: StorageResult; label: string; colour: string }) {
  return (
    <div style={cardStyle}>
      {/* Site header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <div style={{ width: 4, height: 20, borderRadius: 2, background: colour }} />
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#1e293b' }}>{label}</h3>
      </div>

      {/* SAN section */}
      {result.rawSanGb > 0 && (
        <StorageSection
          title="SAN"
          colour="#1e40af"
          rows={[
            { label: 'Usable SAN', value: `${result.usableSanGb.toLocaleString()} GB` },
            { label: 'Raw SAN (after growth + RAID)', value: `${Math.ceil(result.rawSanGb).toLocaleString()} GB`, highlight: true },
          ]}
        />
      )}

      {/* NAS section */}
      {result.rawNasGb > 0 && (
        <StorageSection
          title="NAS"
          colour="#065f46"
          rows={[
            { label: 'Usable NAS', value: `${result.usableNasGb.toLocaleString()} GB` },
            { label: 'Raw NAS (after growth + RAID)', value: `${Math.ceil(result.rawNasGb).toLocaleString()} GB`, highlight: true },
          ]}
        />
      )}

      {/* Local section */}
      {result.rawLocalGb > 0 && (
        <StorageSection
          title="Local / Direct-Attach"
          colour="#7c2d12"
          rows={[
            { label: 'Usable Local', value: `${result.usableLocalGb.toLocaleString()} GB` },
            { label: 'Raw Local (after growth + RAID)', value: `${Math.ceil(result.rawLocalGb).toLocaleString()} GB`, highlight: true },
          ]}
        />
      )}

      {/* IOPS */}
      {result.totalSanIops > 0 && (
        <StorageSection
          title="IOPS"
          colour="#4c1d95"
          rows={[
            { label: 'Nominal SAN IOPS', value: result.totalSanIops.toLocaleString() },
            { label: 'Effective IOPS (after RAID write penalty)', value: Math.ceil(result.effectiveIopsRequired).toLocaleString(), highlight: true },
          ]}
        />
      )}

      {/* Nothing at all */}
      {result.rawSanGb === 0 && result.rawNasGb === 0 && result.rawLocalGb === 0 && result.totalSanIops === 0 && (
        <p style={{ color: '#64748b', fontStyle: 'italic', fontSize: 13 }}>No storage requirements at this site.</p>
      )}

      {/* Methodology notes */}
      {result.notes.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ fontSize: 12, color: '#64748b', cursor: 'pointer', userSelect: 'none' }}>
            Methodology notes ({result.notes.length})
          </summary>
          <ol style={{ margin: '6px 0 0 0', paddingLeft: 18 }}>
            {result.notes.map((n, i) => (
              <li key={i} style={{ fontSize: 12, color: '#475569', marginBottom: 3, lineHeight: 1.5 }}>{n}</li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}

function StorageSection({
  title,
  colour,
  rows,
}: {
  title: string;
  colour: string;
  rows: { label: string; value: string; highlight?: boolean }[];
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        fontSize: 11,
        fontWeight: 700,
        color: colour,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        marginBottom: 6,
      }}>
        {title}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {rows.map((r, i) => (
          <div key={i} style={{
            padding: '6px 8px',
            background: r.highlight ? colour + '0d' : '#fafafa',
            borderRadius: 6,
            border: r.highlight ? `1px solid ${colour}33` : '1px solid #f1f5f9',
          }}>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{r.label}</div>
            <div style={{
              fontSize: 15,
              fontWeight: 700,
              color: r.highlight ? colour : '#1e293b',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {r.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  padding: 16,
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  background: '#fff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
